import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { 
  FaTimes, 
  FaExclamationTriangle, 
  FaInfoCircle,
  FaDatabase,
  FaCopy,
  FaPlus,
  FaExpand
} from 'react-icons/fa';
import { HardDrive } from 'lucide-react';


import { api } from '../../services/api';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'create-volume-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'create-volume-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'create-volume-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please contact support.',
  'INVALID_ID_FORMAT': 'Invalid volume ID format. Please try again.',
  'SERVICE_ERROR': 'Service error. Please contact support.',
  'VOLUME_STATUS_CHECK_FAILED': 'Unable to verify volume status. Please try again.',
  'VOLUME_STATUS_INCONSISTENT': 'Volume status is inconsistent. Please wait a moment and try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'INCOMPLETE_PROFILE': 'Please complete your profile before creating volumes.',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'VOLUME_CREATE_FAILED': 'Unable to create volume. Please try again.',
  'VOLUME_CLONE_FAILED': 'Unable to clone volume. Please try again.',
  'VOLUME_RESIZE_FAILED': 'Unable to resize volume. Please try again.',
  'VOLUME_SIZE_INVALID': 'Invalid volume size. Please check the size requirements.',
  'RESOURCE_ALREADY_EXISTS': 'A volume with this name already exists in your account.',
  'UNSUPPORTED_OPERATION': 'This operation is not supported for this region.',
  'BOOTABLE_VOLUMES_NOT_SUPPORTED': 'Bootable volumes from OS images are not supported in this region.',
  'CROSS_REGION_CLONE_NOT_SUPPORTED': 'Volume cloning across different regions is not supported.',
  'VOLUME_ATTACHED_TO_RUNNING_INSTANCE': 'Volume is attached to a running instance and cannot be cloned.',
  'VOLUME_STATUS_NOT_CLONEABLE': 'Volume cannot be cloned in its current status.',
  'VOLUME_NOT_FOUND': 'Volume not found or does not belong to your account.',
  'INVALID_VOLUME_TYPE': 'Invalid volume type for the selected region.',
  'FEATURE_NOT_SUPPORTED': 'This feature is not supported for the selected region.',
  'BOOTABLE_VOLUME_DETACH_FORBIDDEN': 'Bootable volumes cannot be detached.',
  'OS_VOLUME_DETACH_FORBIDDEN': 'OS volumes cannot be detached.',
  'DATABASE_UPDATE_FAILED': 'Operation completed but database update failed.',
  'INSUFFICIENT_CREDITS': 'Insufficient credits to create this volume. Please add credits to continue.',
  'VOLUME_LIST_FAILED': 'Unable to load volume information. Please try again.',
  'OS_IMAGES_FETCH_FAILED': 'Unable to load OS images. Please try again.',
  'RESOURCE_NOT_FOUND': 'Required resource not found. Please try again.',
  'INVALID_VOLUME_PARAMETERS': 'Invalid volume configuration. Please check your inputs.',
  'VALIDATION_ERROR': 'Invalid input parameters. Please check your entries and try again.',
  'MISSING_FIELDS': 'Required fields are missing. Please complete all required fields.',
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'INVALID_DATA': 'Invalid input data',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again.',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later.',
  'DB_ERROR': 'Service temporarily unavailable',
  // Network Errors (6-position detection)
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
  'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const validateVolumeName = (name) => {
  if (!name || typeof name !== 'string') return { valid: false, error: 'Name is required' };
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 3) return { valid: false, error: 'Volume name must be at least 3 characters' };
  if (trimmedName.length > 30) return { valid: false, error: 'Volume name cannot exceed 30 characters' };
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmedName)) return { valid: false, error: 'Volume name can only contain letters, numbers, hyphens, and underscores' };
  if (trimmedName.startsWith('-') || trimmedName.startsWith('_')) return { valid: false, error: 'Volume name cannot start with a hyphen or underscore' };
  if (trimmedName.endsWith('-') || trimmedName.endsWith('_')) return { valid: false, error: 'Volume name cannot end with a hyphen or underscore' };
  
  return { valid: true };
};
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check error code first (interceptor normalizes to err.code)
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check HTTP status
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.status === 429) {
    return ERROR_MESSAGES['RATE_LIMIT_EXCEEDED'];
  }
  if (error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  if (error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  // Check error message from interceptor
  if (error.error) {
    return error.error;
  }
  
  return 'Something went wrong. Please try again.';
};
// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline (backup for 2.1)
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// REGION & VOLUME HELPERS
// ============================================================================
const getRegionCapabilities = (region) => {
  const advancedRegions = ['FINLAND-1', 'FINLAND-2', 'FINLAND-3'];
  const legacyRegions = ['CANADA-1'];
  
  return {
    supportsAdvancedOperations: advancedRegions.includes(region),
    supportsOSImages: legacyRegions.includes(region),
    isAdvancedRegion: advancedRegions.includes(region),
    isLegacyRegion: legacyRegions.includes(region)
  };
};

const getVolumeDisplayName = (volume) => {
  return volume?.display_name || volume?.name || 'Unknown';
};

const getVolumeTypeDisplay = (volume) => {
  return volume?.volume_type || volume?.type || 'Standard';
};

const mapVolumeStatus = (volume) => {
  const capabilities = getRegionCapabilities(volume?.region);
  
  if (capabilities.isAdvancedRegion) {
    if (volume?.status === 'attached' || volume?.instance_id) return 'Attached';
    return 'Available';
  } else {
    if (volume?.status === 'in-use' || volume?.attached || volume?.instance_id) return 'Attached';
    return 'Available';
  }
};

const getVMStatus = (volume) => {
  const capabilities = getRegionCapabilities(volume?.region);
  
  if (capabilities.isAdvancedRegion && volume?.instances && volume?.instances.length > 0) {
    const status = volume.instances[0].status.toLowerCase();
    if (status === 'running') return 'ACTIVE';
    if (status === 'offline' || status === 'stopped') return 'SHUTOFF';
    if (status === 'provisioning') return 'CREATING';
    if (status === 'shelved_offloaded') return 'HIBERNATED';
    return volume.instances[0].status.toUpperCase();
  }
  return volume?.vm_status;
};

const getVolumeTypesForRegion = (region) => {
  const capabilities = getRegionCapabilities(region);
  if (capabilities.isAdvancedRegion) {
    return ['NVMe', 'HDD'];
  } else {
    return ['SSD'];
  }
};

const isBootableDisabledForRegion = (region) => {
  const capabilities = getRegionCapabilities(region);
  return !capabilities.supportsOSImages;
};

const getMaxVolumeSize = (region, volumeType) => {
  const capabilities = getRegionCapabilities(region);
  
  if (capabilities.isAdvancedRegion) {
    if (volumeType === 'HDD') {
      return 16000;
    } else {
      return 10000;
    }
  } else {
    return 102400;
  }
};

const currencyUtils = {
  getSymbol: (currencyCode) => {
    switch(currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      default: return '$';
    }
  }
};

const validateVolumeStatusForClone = (volume) => {
  if (!volume) return { valid: false, error: 'Volume data not available' };
  
  const volumeStatus = mapVolumeStatus(volume);
  const vmStatus = getVMStatus(volume);
  
  if (volumeStatus === 'Available') {
    return { valid: true };
  }
  
  if (volumeStatus === 'Attached') {
    if (!vmStatus || vmStatus === 'SHUTOFF') {
      return { valid: true };
    } else {
      return { 
        valid: false, 
        error: `Volume is attached to a ${vmStatus} instance. Stop the instance before cloning.`
      };
    }
  }
  
  return { valid: false, error: 'Volume cannot be cloned in its current status' };
};

const validateVolumeStatusForResize = (volume) => {
  if (!volume) return { valid: false, error: 'Volume data not available' };
  
  const volumeStatus = mapVolumeStatus(volume);
  
  if (volumeStatus === 'Available' || volumeStatus === 'Attached') {
    return { valid: true };
  }
  
  return { 
    valid: false, 
    error: 'Volume cannot be resized in its current status.'
  };
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '720px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '260px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '28px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '13px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    maxHeight: '200px',
    overflow: 'auto',
  },
  dropdownItem: {
    padding: '10px 14px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  infoBox: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '18px',
  },
  infoText: {
    color: '#93c5fd',
    fontSize: '12px',
    margin: 0,
  },
  sourceCard: {
    backgroundColor: '#1a1a1a',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '20px',
    border: '1px solid #333',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
  priceValue: {
    color: '#e0eacc',
    fontSize: '32px',
    fontWeight: '700',
    margin: 0,
  },
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CreateVolumeModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onClone,
  onResize,
  regions, 
  images, 
  existingVolumeNames, 
  initialName, 
  selectedRegion: parentSelectedRegion,
  userCurrency = 'USD',
  mode = 'create',
  sourceVolume = null
}) => {
  const { handleAuthError } = useAuth();
  
  // Form states
  const [name, setName] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [size, setSize] = useState(100);
  const [volumeType, setVolumeType] = useState('');
  const [isBootable, setIsBootable] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [error, setError] = useState(null);
  
  // Size input states
  const [sizeInputFocused, setSizeInputFocused] = useState(false);
  const [sizeInputValue, setSizeInputValue] = useState('100 GB');
  const [isSizeInputValid, setIsSizeInputValid] = useState(true);
  
  // Loading states
  const [loadingStates, setLoadingStates] = useState({
    fetchingName: false,
    fetchingImages: false,
    creating: false,
    cloning: false,
    resizing: false,
    checkingStatus: false
  });
  
  // Validation states
  const [nameError, setNameError] = useState('');
  const [sizeError, setSizeError] = useState('');
  const [imageError, setImageError] = useState('');
  const [volumeTypeError, setVolumeTypeError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);

  // Dropdown states
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [isImageDropdownOpen, setIsImageDropdownOpen] = useState(false);
  const [isVolumeTypeDropdownOpen, setIsVolumeTypeDropdownOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);

  // Refs
  const regionDropdownRef = useRef(null);
  const imageDropdownRef = useRef(null);
  const volumeTypeDropdownRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  // Clone/Resize states
  const [volumeStatusValid, setVolumeStatusValid] = useState(true);
  const [freshVolumeData, setFreshVolumeData] = useState(null);
  
  // Derived values
  const isSubmitting = loadingStates.creating || loadingStates.cloning || loadingStates.resizing;
  const styles = getStyles(isSubmitting);
  
  const modalTitle = mode === 'clone' ? 'Clone Volume' : mode === 'resize' ? 'Resize Volume' : 'Create Volume';
  const modalSubtitle = mode === 'clone' ? 'Duplicate existing volume' : mode === 'resize' ? 'Expand storage capacity' : 'Configure persistent storage';
  const actionText = mode === 'clone' ? 'Cloning...' : mode === 'resize' ? 'Resizing...' : 'Creating...';
  const buttonText = mode === 'clone' ? 'Clone Volume' : mode === 'resize' ? 'Resize Volume' : 'Create Volume';
  const buttonIcon = mode === 'clone' ? <FaCopy size={14} /> : mode === 'resize' ? <FaExpand size={14} /> : <FaPlus size={14} />;
  
  // Memoized values
  const maxVolumeSize = useMemo(() => {
    return getMaxVolumeSize(selectedRegion, volumeType);
  }, [selectedRegion, volumeType]);
  
  const availableVolumeTypes = useMemo(() => {
    return getVolumeTypesForRegion(selectedRegion);
  }, [selectedRegion]);
  
  const isBootableDisabled = useMemo(() => {
    return mode === 'clone' || mode === 'resize' || isBootableDisabledForRegion(selectedRegion);
  }, [selectedRegion, mode]);

  const cloneRegionRestrictions = useMemo(() => {
    if (mode !== 'clone' || !sourceVolume) return [];
    const capabilities = getRegionCapabilities(sourceVolume.region);
    if (capabilities.isAdvancedRegion) {
      return [sourceVolume.region];
    }
    return regions;
  }, [mode, sourceVolume, regions]);

const currentPricing = useMemo(() => {
    const currency = userCurrency;
    const currentSize = (mode === 'clone' && sourceVolume) ? sourceVolume.size : size;
    const capabilities = getRegionCapabilities(selectedRegion);
    let pricing;
    
    if (capabilities.isLegacyRegion) {
      pricing = { 'USD': 0.07129, 'EUR': 0.07129, 'INR': 7.12891 };
    } else {
      if (volumeType === 'HDD') {
        pricing = { 'USD': 0.05703, 'EUR': 0.07129, 'INR': 5.70313 };
      } else {
        pricing = { 'USD': 0.21387, 'EUR': 0.24951, 'INR': 21.38672 };
      }
    }
    
    const pricePerGB = pricing[currency] || 0.1;
    const symbol = currencyUtils.getSymbol(currency);
    
    return {
      perGB: pricePerGB,
      currency,
      symbol,
      monthly: (currentSize * pricePerGB).toFixed(2),
      formatted: `${symbol}${pricePerGB}/GB/month`
    };
}, [selectedRegion, volumeType, size, mode, sourceVolume, userCurrency]);

  const filteredImages = useMemo(() => {
    const sourceImages = modalImages.length > 0 ? modalImages : images;
    if (!Array.isArray(sourceImages)) return [];
    return sourceImages.filter(image => image && image.name);
  }, [modalImages, images]);

  const validationResults = useMemo(() => {
    const results = {
      nameValid: true,
      sizeValid: true,
      imageValid: true,
      regionValid: true,
      volumeTypeValid: true,
      statusValid: true
    };

    if (!name.trim()) {
      results.nameValid = false;
    } else if (name.length > 30) {
      results.nameValid = false;
    } else if (mode !== 'resize' && existingVolumeNames && existingVolumeNames.includes(name)) {
      results.nameValid = false;
    }

    if (mode === 'resize') {
      if (size <= (sourceVolume?.size || 0)) {
        results.sizeValid = false;
      } else if (size > maxVolumeSize) {
        results.sizeValid = false;
      }
    } else if (mode === 'clone') {
      results.sizeValid = true;
    } else {
      if (size < 100) {
        results.sizeValid = false;
      } else if (size > maxVolumeSize) {
        results.sizeValid = false;
      }
    }

    if (isBootable && !selectedImage) {
      results.imageValid = false;
    }

    if (!selectedRegion) {
      results.regionValid = false;
    }
    
    if (!volumeType) {
      results.volumeTypeValid = false;
    }
    
    if ((mode === 'clone' || mode === 'resize') && !volumeStatusValid) {
      results.statusValid = false;
    }

    return results;
  }, [name, size, isBootable, selectedImage, selectedRegion, volumeType, existingVolumeNames, maxVolumeSize, mode, volumeStatusValid, sourceVolume]);

  // Effects
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("create-volume-modal", {
        isOpen: true,
        mode,
        initialName: initialName || null,
        availableRegions: regions?.length || 0,
        parentSelectedRegion: parentSelectedRegion || null,
      });
      
      logInfo(`${modalTitle} modal opened`, { mode });
    }
  }, [isOpen, mode, modalTitle, initialName, regions?.length, parentSelectedRegion]);
  
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
      if (imageDropdownRef.current && !imageDropdownRef.current.contains(event.target)) {
        setIsImageDropdownOpen(false);
      }
      if (volumeTypeDropdownRef.current && !volumeTypeDropdownRef.current.contains(event.target)) {
        setIsVolumeTypeDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
    if (isBootableDisabled) {
      setIsBootable(false);
      setSelectedImage('');
      setImageError('');
    }
  }, [selectedRegion, isBootableDisabled]);
  
  useEffect(() => {
    if (selectedRegion && availableVolumeTypes.length > 0) {
      let defaultType = availableVolumeTypes[0];
      if ((mode === 'clone' || mode === 'resize') && sourceVolume) {
        const sourceVolumeType = getVolumeTypeDisplay(sourceVolume);
        if (availableVolumeTypes.includes(sourceVolumeType)) {
          defaultType = sourceVolumeType;
        }
      }
      setVolumeType(defaultType);
      setVolumeTypeError('');
    }
  }, [selectedRegion, availableVolumeTypes, mode, sourceVolume]);
  
  useEffect(() => {
    if (isBootable && size < 100) {
      setSize(100);
      setSizeInputValue('100 GB');
      setSizeError('');
    }
    
    if (size > maxVolumeSize) {
      setSize(maxVolumeSize);
      setSizeInputValue(`${maxVolumeSize} GB`);
    }
  }, [isBootable, size, maxVolumeSize]);

const validateVolumeStatusRealTime = useCallback(async (volumeId, region) => {
  const startTime = performance.now();
  
  setLoadingStates(prev => ({ ...prev, checkingStatus: true }));
  setStatusError('');
  setFreshVolumeData(null);
  
  try {
    const response = await api.getVolumesWithNextName(region);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success && response.volumes) {
      const freshVolume = response.volumes.find(vol => vol.id === volumeId);
      
      if (freshVolume) {
        setFreshVolumeData(freshVolume);
        
        const validation = mode === 'clone' 
          ? validateVolumeStatusForClone(freshVolume)
          : validateVolumeStatusForResize(freshVolume);
        setVolumeStatusValid(validation.valid);
        
        if (!validation.valid) {
          setStatusError(validation.error);
          setError(validation.error);
        } else {
          setStatusError('');
          setError(null);
        }
        
        logPerformance('real_time_volume_status_validation', duration, {
          volumeId,
          isValid: validation.valid,
          mode,
        });
      } else {
        setVolumeStatusValid(false);
        const notFoundError = 'Volume not found or no longer accessible';
        setStatusError(notFoundError);
        setError(notFoundError);
      }
    } else {
      const apiError = 'Unable to verify volume status. Please try again.';
      setVolumeStatusValid(false);
      setStatusError(apiError);
      setError(apiError);
    }
  } catch (err) {
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, checkingStatus: false }));
      return;
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      setVolumeStatusValid(false);
      setStatusError(err.error);
      setError(err.error);
      logError('Network error in validateVolumeStatusRealTime', { 
        code: err.code, 
        volumeId, 
        mode 
      }, err);
      setLoadingStates(prev => ({ ...prev, checkingStatus: false }));
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setVolumeStatusValid(false);
      setStatusError(err.error);
      setError(err.error);
      setLoadingStates(prev => ({ ...prev, checkingStatus: false }));
      return;
    }
    
    // Position 4: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setVolumeStatusValid(false);
      setStatusError(ERROR_MESSAGES[err.code]);
      setError(ERROR_MESSAGES[err.code]);
      logError('Volume status validation error - mapped code', {
        code: err.code,
        volumeId,
        mode
      }, err);
      setLoadingStates(prev => ({ ...prev, checkingStatus: false }));
      return;
    }
    
    // Position 5: Fallback
    const safeMessage = err.error || 'Unable to verify volume status. Please try again.';
    setVolumeStatusValid(false);
    setStatusError(safeMessage);
    setError(safeMessage);
    
    logError('Error during volume status validation', { volumeId, mode }, err);
  } finally {
    setLoadingStates(prev => ({ ...prev, checkingStatus: false }));
  }
}, [mode, handleAuthError]);
  useEffect(() => {
    if ((mode === 'clone' || mode === 'resize') && sourceVolume && isOpen) {
      validateVolumeStatusRealTime(sourceVolume.id, sourceVolume.region);
    }
  }, [mode, sourceVolume, isOpen, validateVolumeStatusRealTime]);

  // Fetch next volume 
  
const debouncedFetchNextVolumeName = useCallback(
  debounce(async () => {
    if (retryAttempts.current >= maxRetries) {
      setError('Maximum fetch attempts reached.');
      return;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    retryAttempts.current++;
    setLoadingStates(prev => ({ ...prev, fetchingName: true }));
    setError(null);
    
    try {
      const response = await api.getVolumesWithNextName();

      if (abortControllerRef.current?.signal.aborted) return;

      if (response.success) {
        setName(response.next_volume_name);
        setNameCharCount(response.next_volume_name.length);
        retryAttempts.current = 0;
      } else {
        setName('volume1');
        setNameCharCount(7);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, fetchingName: false }));
        return;
      }
      
      // Position 2: Network error - silent fallback
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        logError('Network error in debouncedFetchNextVolumeName', { 
          code: err.code
        }, err);
        setName('volume1');
        setNameCharCount(7);
        setLoadingStates(prev => ({ ...prev, fetchingName: false }));
        return;
      }
      
      // Position 3: Rate limit - silent fallback
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        logError('Rate limit in debouncedFetchNextVolumeName', {
          code: err.code
        }, err);
        setName('volume1');
        setNameCharCount(7);
        setLoadingStates(prev => ({ ...prev, fetchingName: false }));
        return;
      }
      
      // Position 4: Mapped error code - silent fallback
      if (err.code && ERROR_MESSAGES[err.code]) {
        logError('Error code in debouncedFetchNextVolumeName', {
          code: err.code
        }, err);
        setName('volume1');
        setNameCharCount(7);
        setLoadingStates(prev => ({ ...prev, fetchingName: false }));
        return;
      }
      
      // Position 5: Fallback - use default name
      setName('volume1');
      setNameCharCount(7);
      logError('Error fetching next volume name', {}, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, fetchingName: false }));
    }
  }, 300),
  [handleAuthError]
);
  // Fetch OS images

const debouncedFetchOsImages = useCallback(
  debounce(async (region) => {
    if (!region) return;
    
    setLoadingStates(prev => ({ ...prev, fetchingImages: true }));
    setError(null);
    
    try {
      const response = await api.getImages(region);

      if (response.success) {
        const fetchedImages = response.images || [];
        const ubuntuImages = fetchedImages.filter(img => 
          img.image_type && img.image_type.toLowerCase().includes('ubuntu')
        );
        setModalImages(ubuntuImages);
      } else {
        setModalImages([]);
      }
    } catch (err) {
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, fetchingImages: false }));
        return;
      }
      
      // Position 2: Network error - silent fallback
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        logError('Network error in debouncedFetchOsImages', { 
          code: err.code,
          region
        }, err);
        setModalImages([]);
        setLoadingStates(prev => ({ ...prev, fetchingImages: false }));
        return;
      }
      
      // Position 3: Rate limit - silent fallback
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        logError('Rate limit in debouncedFetchOsImages', {
          code: err.code,
          region
        }, err);
        setModalImages([]);
        setLoadingStates(prev => ({ ...prev, fetchingImages: false }));
        return;
      }
      
      // Position 4: Mapped error code - silent fallback
      if (err.code && ERROR_MESSAGES[err.code]) {
        logError('Error code in debouncedFetchOsImages', {
          code: err.code,
          region
        }, err);
        setModalImages([]);
        setLoadingStates(prev => ({ ...prev, fetchingImages: false }));
        return;
      }
      
      // Position 5: Fallback - empty images array
      setModalImages([]);
      logError('Error fetching OS images', { region }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, fetchingImages: false }));
    }
  }, 300),
  [handleAuthError]
);  // Initialize form on modal open
  useEffect(() => {
    if (isOpen) {
      retryAttempts.current = 0;
      
      setLoadingStates({
        fetchingName: false,
        fetchingImages: false,
        creating: false,
        cloning: false,
        resizing: false,
        checkingStatus: false
      });
      
      if (mode === 'clone' && sourceVolume) {
        const cloneName = `${getVolumeDisplayName(sourceVolume)}-clone`;
        setName(cloneName);
        setNameCharCount(cloneName.length);
        setSize(sourceVolume.size || 100);
        setSizeInputValue(`${sourceVolume.size || 100} GB`);
        setSelectedRegion(sourceVolume.region || '');
        setIsBootable(false);
        setSelectedImage('');
      } else if (mode === 'resize' && sourceVolume) {
        setName(getVolumeDisplayName(sourceVolume));
        setNameCharCount(getVolumeDisplayName(sourceVolume).length);
        setSize(sourceVolume.size || 100);
        setSizeInputValue(`${sourceVolume.size || 100} GB`);
        setSelectedRegion(sourceVolume.region || '');
        setIsBootable(false);
        setSelectedImage('');
      } else {
        if (initialName) {
          setName(initialName);
          setNameCharCount(initialName.length);
        } else {
          debouncedFetchNextVolumeName();
        }
        
        const defaultRegion = parentSelectedRegion || (regions && regions.length > 0 ? regions[0] : '');
        setSelectedRegion(defaultRegion);
        setSize(100);
        setSizeInputValue('100 GB');
        setIsBootable(false);
        setSelectedImage('');
      }
      
      setError(null);
      setNameError('');
      setSizeError('');
      setImageError('');
      setVolumeTypeError('');
      setStatusError('');
      setIsRegionDropdownOpen(false);
      setIsImageDropdownOpen(false);
      setIsVolumeTypeDropdownOpen(false);
      setFreshVolumeData(null);
      setModalImages([]);
      setVolumeStatusValid(true);
      setSizeInputFocused(false);
      setIsSizeInputValid(true);
    }
  }, [isOpen, regions, initialName, parentSelectedRegion, debouncedFetchNextVolumeName, mode, sourceVolume]);

  // Handlers
  const handleNameChange = useCallback((e) => {
    const newName = sanitizeText(e.target.value);
    setName(newName);
    setNameCharCount(newName.length);
    setError(null);
    
    const trimmedName = newName.trim();
    const validation = validateVolumeName(trimmedName);
    
    if (!validation.valid) {
      setNameError(validation.error);
    } else if (mode !== 'resize' && existingVolumeNames && existingVolumeNames.includes(trimmedName)) {
      setNameError('A volume with this name already exists');
    } else {
      setNameError('');
    }
  }, [existingVolumeNames, mode]);
  
  const handleSizeInputChange = useCallback((e) => {
    if (mode === 'clone') return;
    
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue === '') {
      setSizeInputValue('GB');
      setIsSizeInputValid(false);
      return;
    }
    
    const formattedValue = `${numericValue} GB`;
    setSizeInputValue(formattedValue);
    
    const parsed = parseInt(numericValue);
    
    let isValid = false;
    if (mode === 'resize') {
      isValid = parsed > (sourceVolume?.size || 0) && parsed <= maxVolumeSize;
    } else {
      isValid = parsed >= 100 && parsed <= maxVolumeSize;
    }
    
    if (isValid) {
      setIsSizeInputValid(true);
      setSize(parsed);
      setSizeError('');
    } else {
      setIsSizeInputValid(false);
    }
    
    setError(null);
  }, [mode, sourceVolume, maxVolumeSize]);

  const handleSizeInputKeyDown = useCallback((e) => {
    if (mode === 'clone') return;
    
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      
      if (e.keyCode === 38) {
        e.preventDefault();
        const newValue = Math.min(size + 100, maxVolumeSize);
        setSize(newValue);
        setSizeInputValue(`${newValue} GB`);
        setIsSizeInputValid(true);
        setSizeError('');
      } else if (e.keyCode === 40) {
        e.preventDefault();
        const minValue = mode === 'resize' ? (sourceVolume?.size || 0) + 1 : 100;
        const newValue = Math.max(size - 100, minValue);
        setSize(newValue);
        setSizeInputValue(`${newValue} GB`);
        setIsSizeInputValid(true);
        setSizeError('');
      }
      
      return;
    }
    
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  }, [mode, size, maxVolumeSize, sourceVolume]);

  const handleSizeInputBlur = useCallback((e) => {
    if (mode === 'clone') return;
    
    setSizeInputFocused(false);
    const value = e.target.value.replace(/\D/g, '');
    
    if (value === '') {
      const defaultValue = mode === 'resize' ? (sourceVolume?.size || 0) + 1 : 100;
      setSize(defaultValue);
      setSizeInputValue(`${defaultValue} GB`);
      setIsSizeInputValid(true);
      setSizeError('');
      return;
    }
    
    const parsed = parseInt(value);
    let finalValue = parsed;
    
    if (mode === 'resize') {
      const minSize = (sourceVolume?.size || 0) + 1;
      if (parsed <= (sourceVolume?.size || 0)) {
        finalValue = minSize;
      } else if (parsed > maxVolumeSize) {
        finalValue = maxVolumeSize;
      }
    } else {
      if (parsed < 100) {
        finalValue = 100;
      } else if (parsed > maxVolumeSize) {
        finalValue = maxVolumeSize;
      }
    }
    
    setSize(finalValue);
    setSizeInputValue(`${finalValue} GB`);
    setIsSizeInputValid(true);
    setSizeError('');
  }, [mode, sourceVolume, maxVolumeSize]);

  const handleSizeInputFocus = useCallback((e) => {
    if (mode === 'clone') return;
    setSizeInputFocused(true);
    setSizeInputValue(`${size} GB`);
  }, [mode, size]);

const handleBootableChange = useCallback((e) => {
    const bootable = e.target.checked;
    setIsBootable(bootable);
    setError(null);
    
    if (bootable) {
      setLoadingStates(prev => ({ ...prev, fetchingImages: true }));
      if (size < 100) {
        setSize(100);
        setSizeInputValue('100 GB');
        setSizeError('');
      }
      
      if (selectedRegion) {
        debouncedFetchOsImages(selectedRegion);
      }
    } else {
      setSelectedImage('');
      setImageError('');
    }
  }, [size, selectedRegion, debouncedFetchOsImages]);
  
  const handleRegionSelect = useCallback((value) => {
    setSelectedRegion(value);
    setIsRegionDropdownOpen(false);
    setError(null);
    
    const newRegionSupportsOSImages = !isBootableDisabledForRegion(value);
    
    if (isBootable && newRegionSupportsOSImages) {
      debouncedFetchOsImages(value);
    }
  }, [isBootable, debouncedFetchOsImages]);

  const handleImageSelect = useCallback((imageName) => {
    setSelectedImage(imageName);
    setIsImageDropdownOpen(false);
    setImageError('');
    setError(null);
  }, []);

  const handleVolumeTypeSelect = useCallback((type) => {
    setVolumeType(type);
    setIsVolumeTypeDropdownOpen(false);
    setVolumeTypeError('');
    setError(null);
  }, []);
  
  const validateForm = useCallback(() => {
    let isValid = true;
    
    if (!name.trim()) {
      setNameError('Volume name is required');
      setError('Volume name is required');
      isValid = false;
    } else if (mode !== 'resize' && existingVolumeNames && existingVolumeNames.includes(name)) {
      setNameError('A volume with this name already exists');
      setError('A volume with this name already exists');
      isValid = false;
    }
    
    if (mode === 'resize') {
      if (size <= (sourceVolume?.size || 0)) {
        setSizeError(`New size must be larger than current size (${sourceVolume?.size} GB)`);
        setError(`New size must be larger than current size (${sourceVolume?.size} GB)`);
        isValid = false;
      } else if (size > maxVolumeSize) {
        setSizeError(`Size cannot exceed ${maxVolumeSize.toLocaleString()} GB`);
        setError(`Size cannot exceed ${maxVolumeSize.toLocaleString()} GB`);
        isValid = false;
      }
    } else if (mode !== 'clone') {
      if (size < 100) {
        setSizeError('Volume size must be at least 100 GB');
        setError('Volume size must be at least 100 GB');
        isValid = false;
      } else if (size > maxVolumeSize) {
        setSizeError(`Size cannot exceed ${maxVolumeSize.toLocaleString()} GB`);
        setError(`Size cannot exceed ${maxVolumeSize.toLocaleString()} GB`);
        isValid = false;
      }
    }
    
    if (isBootable && !selectedImage) {
      setImageError('An OS image is required for bootable volumes');
      setError('An OS image is required for bootable volumes');
      isValid = false;
    }
    
    if (!selectedRegion) {
      setError('Please select a region');
      isValid = false;
    }
    
    if (!volumeType) {
      setVolumeTypeError('Please select a volume type');
      setError('Please select a volume type');
      isValid = false;
    }
    
    if ((mode === 'clone' || mode === 'resize') && !volumeStatusValid) {
      setError(statusError || `Volume cannot be ${mode}d in its current status`);
      isValid = false;
    }
    
    return isValid;
  }, [name, existingVolumeNames, size, isBootable, selectedImage, selectedRegion, volumeType, maxVolumeSize, mode, volumeStatusValid, statusError, sourceVolume]);
  
  const handleSubmit = useCallback(async () => {
    const startTime = performance.now();
    
    logUserAction(`volume_${mode}_submit_initiated`, {
      name,
      size,
      selectedRegion,
      volumeType,
      mode,
    });
    
    setError(null);
    const loadingKey = mode === 'clone' ? 'cloning' : mode === 'resize' ? 'resizing' : 'creating';
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
    
    if (!validateForm()) {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      return;
    }
    
    try {
      let response;
      
      if (mode === 'clone') {
        const cloneData = {
          clone_name: sanitizeText(name.trim()),
          target_region: selectedRegion,
          volume_type: volumeType,
        };
        
        response = await onClone(sourceVolume.id, cloneData.clone_name, cloneData.target_region, cloneData.volume_type);
      } else if (mode === 'resize') {
        response = await onResize(sourceVolume.id, parseInt(size));
      } else {
        const volumeData = {
          name: sanitizeText(name.trim()),
          region: selectedRegion,
          size: parseInt(size),
          volume_type: volumeType,
        };
        
        if (isBootable && selectedImage) {
          const capabilities = getRegionCapabilities(selectedRegion);
          if (capabilities.supportsOSImages) {
            volumeData.image_name = selectedImage;
          }
        }
        
        response = await onSave(volumeData);
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response === false || (response && !response.success)) {
        setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
        logError(`Volume ${mode} failed`, { response, duration });
        return;
      }
      
      onClose(response);
      
      logUserAction(`volume_${mode}_success`, {
        volumeName: name,
        duration: `${duration.toFixed(2)}ms`,
        mode,
      });
    } catch (err) {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      logError(`Error ${mode} volume`, { name, mode }, err);
    }
  }, [validateForm, name, selectedRegion, size, volumeType, isBootable, selectedImage, onSave, onClone, onResize, onClose, mode, sourceVolume]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    logUserAction(`close_${mode}_volume_modal`, { name, mode });
    onClose();
  }, [onClose, name, mode, isSubmitting]);
  
  if (!isOpen) return null;
  
  const availableRegions = mode === 'clone' ? cloneRegionRestrictions : regions;

  // Render button content
  const renderButtonContent = () => {
    if (isSubmitting) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={styles.spinner}></div>
          <span>{actionText}</span>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {buttonIcon}
        <span>{buttonText}</span>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .volume-modal-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* LEFT PANEL - Configuration */}
          <div style={styles.leftPanel}>
            {/* Header */}
<div style={styles.header}>
  <div style={styles.iconWrapper}>
    {mode === 'clone' ? <FaCopy size={20} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} /> 
      : mode === 'resize' ? <FaExpand size={20} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} /> 
      : <HardDrive size={20} strokeWidth={1.5} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} />}
  </div>
  <div>
    <h2 style={styles.title}>{modalTitle}</h2>
    <p style={styles.subtitle}>{modalSubtitle}</p>
  </div>
</div>
            {/* Error Display */}
            {error && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(error)}</span>
              </div>
            )}

            {/* Clone/Resize Source Info */}
            {(mode === 'clone' || mode === 'resize') && sourceVolume && (
              <div style={styles.sourceCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FaInfoCircle style={{ color: '#e0eacc' }} />
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                    {mode === 'clone' ? 'Cloning from:' : 'Resizing:'}
                  </span>
                </div>
                <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                  {sanitizeText(getVolumeDisplayName(sourceVolume))}
                </div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  {sourceVolume.size} GB • {sanitizeText(getVolumeTypeDisplay(sourceVolume))} • {sourceVolume.region}
                </div>
                {freshVolumeData && (
                  <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px' }}>
                    Status: {mapVolumeStatus(freshVolumeData)}
                    {freshVolumeData.vm_status && ` • VM: ${freshVolumeData.vm_status}`}
                  </div>
                )}
              </div>
            )}

            {/* Status checking indicator */}
            {loadingStates.checkingStatus && (
              <div style={styles.infoBox}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ ...styles.spinner, borderTopColor: '#93c5fd', border: '2px solid rgba(147,197,253,0.2)' }}></div>
                  <span style={styles.infoText}>Verifying volume status...</span>
                </div>
              </div>
            )}

         
            {/* RESIZE MODE - Simplified */}
            {mode === 'resize' ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={styles.label}>
                      New Size (GB)<span style={styles.required}>*</span>
                    </label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                      Current: {sourceVolume?.size} GB
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Custom Slider Track */}
                    <div
                      style={{
                        flex: 1,
                        height: '4px',
                        background: isSubmitting ? '#222' : '#333333',
                        borderRadius: '2px',
                        position: 'relative',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s ease',
                        opacity: isSubmitting ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.background = '#444444';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.background = '#333333';
                        }
                      }}
                      onMouseDown={(e) => {
                        if (isSubmitting) return;
                        if (e.target === e.currentTarget) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const minSize = (sourceVolume?.size || 0) + 1;
                          const percent = Math.max(0, Math.min(1, clickX / rect.width));
                          const newValue = Math.round(minSize + percent * (maxVolumeSize - minSize));
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setError(null);
                          e.preventDefault();
                        }
                      }}
                    >
                      {/* Thumb */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((size - ((sourceVolume?.size || 0) + 1)) / (maxVolumeSize - ((sourceVolume?.size || 0) + 1))) * 100}%`,
                          top: '-5px',
                          width: '14px',
                          height: '14px',
                          background: isSubmitting ? '#666' : '#e0eacc',
                          borderRadius: '50%',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
                          transition: 'background 0.2s ease, opacity 0.2s ease',
                          pointerEvents: 'none',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                      />
                      {/* Invisible Hit Area */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((size - ((sourceVolume?.size || 0) + 1)) / (maxVolumeSize - ((sourceVolume?.size || 0) + 1))) * 100}%`,
                          top: '-15px',
                          width: '34px',
                          height: '34px',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
                          zIndex: 1,
                        }}
                        onMouseDown={(e) => {
                          if (isSubmitting) return;
                          const trackRect = e.currentTarget.parentElement.getBoundingClientRect();
                          const minSize = (sourceVolume?.size || 0) + 1;
                          
                          const handleMouseMove = (moveEvent) => {
                            const newPercent = Math.max(0, Math.min(1, (moveEvent.clientX - trackRect.left) / trackRect.width));
                            const newVal = Math.round(minSize + newPercent * (maxVolumeSize - minSize));
                            setSize(newVal);
                            setSizeInputValue(`${newVal} GB`);
                          };
                          
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    {/* Increment/Decrement Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        style={{
                          width: '18px',
                          height: '12px',
                          background: '#333',
                          border: 'none',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          color: '#999',
                          transition: 'all 0.1s',
                          borderRadius: '2px 2px 0 0',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValue = Math.min(size + 100, maxVolumeSize);
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setIsSizeInputValid(true);
                          setSizeError('');
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#333';
                          e.target.style.color = '#999';
                        }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        style={{
                          width: '18px',
                          height: '12px',
                          background: '#333',
                          border: 'none',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          color: '#999',
                          transition: 'all 0.1s',
                          borderRadius: '0 0 2px 2px',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const minSize = (sourceVolume?.size || 0) + 1;
                          const newValue = Math.max(size - 100, minSize);
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setIsSizeInputValid(true);
                          setSizeError('');
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#333';
                          e.target.style.color = '#999';
                        }}
                      >
                        ▼
                      </button>
                    </div>
          <input
  type="text"
  value={sizeInputValue}
  onChange={handleSizeInputChange}
  onFocus={(e) => {
    handleSizeInputFocus(e);
    e.target.style.borderColor = '#e0eacc';
    e.target.style.backgroundColor = '#252525';
    e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
  }}
  onBlur={(e) => {
    handleSizeInputBlur(e);
    e.target.style.borderColor = isSizeInputValid ? '#333' : '#ff6b6b';
    e.target.style.backgroundColor = '#1a1a1a';
    e.target.style.boxShadow = 'none';
  }}
  onKeyDown={handleSizeInputKeyDown}
  disabled={isSubmitting}
  style={{
    ...styles.input,
    width: '100px',
    textAlign: 'center',
    borderColor: isSizeInputValid ? '#333' : '#ff6b6b',
    transition: 'all 0.15s',
  }}
/>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                    <span style={{ color: '#444', fontSize: '10px' }}>{sourceVolume?.size + 1} GB</span>
                    <span style={{ color: '#444', fontSize: '10px' }}>{maxVolumeSize.toLocaleString()} GB</span>
                  </div>
                  {sizeError && <div style={styles.errorText}>{sizeError}</div>}
                </div>
              </div>
            ) : (
              /* CREATE/CLONE MODE */
              <>
                {/* Name Field */}
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <label style={styles.label}>
                      Volume Name<span style={styles.required}>*</span>
                    </label>
                    <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                      {nameCharCount}/30
                    </span>
                  </div>
                  <input
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    placeholder="Enter volume name"
                    disabled={isSubmitting}
                    maxLength={30}
                    style={{
                      ...styles.input,
                      borderColor: nameError ? '#ff6b6b' : '#333',
                    }}
                    onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                    onBlur={(e) => e.target.style.borderColor = nameError ? '#ff6b6b' : '#333'}
                  />
                  {nameError && <div style={styles.errorText}>{nameError}</div>}
                </div>

                {/* Region & Type Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                  {/* Region Dropdown */}
                  <div style={styles.dropdown} ref={regionDropdownRef}>
                    <label style={styles.label}>
                      Region<span style={styles.required}>*</span>
                    </label>
                    <div
                      style={{
                        ...styles.dropdownTrigger,
                        opacity: mode === 'clone' ? 0.6 : 1,
                        cursor: mode === 'clone' || isSubmitting ? 'not-allowed' : 'pointer',
                        borderColor: isRegionDropdownOpen ? '#e0eacc' : '#333',
                      }}
                      onClick={() => mode !== 'clone' && !isSubmitting && setIsRegionDropdownOpen(!isRegionDropdownOpen)}
                    >
                      <span>{selectedRegion || 'Select region'}</span>
                      {mode !== 'clone' && <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>}
                    </div>
                    {isRegionDropdownOpen && !isSubmitting && (
                      <div style={styles.dropdownMenu} className="volume-modal-scrollbar">
                        {availableRegions && availableRegions.map((r) => (
                          <div
                            key={r}
                            style={{
                              ...styles.dropdownItem,
                              backgroundColor: selectedRegion === r ? '#252525' : 'transparent',
                            }}
                            onClick={() => handleRegionSelect(r)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = selectedRegion === r ? '#252525' : 'transparent'}
                          >
                            {r}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Volume Type Dropdown */}
                  <div style={styles.dropdown} ref={volumeTypeDropdownRef}>
                    <label style={styles.label}>
                      Type<span style={styles.required}>*</span>
                    </label>
                    <div
                      style={{
                        ...styles.dropdownTrigger,
                        opacity: mode === 'clone' ? 0.6 : 1,
                        cursor: mode === 'clone' || isSubmitting ? 'not-allowed' : 'pointer',
                        borderColor: isVolumeTypeDropdownOpen ? '#e0eacc' : volumeTypeError ? '#ff6b6b' : '#333',
                      }}
                      onClick={() => mode !== 'clone' && !isSubmitting && setIsVolumeTypeDropdownOpen(!isVolumeTypeDropdownOpen)}
                    >
                      <span>{volumeType || 'Select type'}</span>
                      {mode !== 'clone' && <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>}
                    </div>
                    {isVolumeTypeDropdownOpen && !isSubmitting && (
                      <div style={styles.dropdownMenu} className="volume-modal-scrollbar">
                        {availableVolumeTypes.map((t) => (
                          <div
                            key={t}
                            style={{
                              ...styles.dropdownItem,
                              backgroundColor: volumeType === t ? '#252525' : 'transparent',
                            }}
                            onClick={() => handleVolumeTypeSelect(t)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = volumeType === t ? '#252525' : 'transparent'}
                          >
                            <div>{t}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {currencyUtils.getSymbol(userCurrency)}{t === 'HDD' ? '0.057' : t === 'NVMe' ? '0.214' : '0.071'}/GB/mo
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {volumeTypeError && <div style={styles.errorText}>{volumeTypeError}</div>}
                  </div>
                </div>

                {/* Size Field - Create mode only */}
         {/* Size Field with Bootable Toggle - Create mode only */}
{mode === 'create' && (
  <div style={{ marginBottom: '18px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <label style={styles.label}>
        Size (GB)<span style={styles.required}>*</span>
      </label>
      {/* Bootable Toggle - Moved here */}
      {!isBootableDisabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#6b7280', fontSize: '11px' }}>Bootable</span>
          <div
            onClick={() => {
              if (!isSubmitting) {
                handleBootableChange({ target: { checked: !isBootable } });
              }
            }}
            style={{
              width: '36px',
              height: '20px',
              borderRadius: '10px',
              backgroundColor: isBootable ? '#e0eacc' : '#444',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              position: 'relative',
              transition: 'all 0.2s',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '8px',
              backgroundColor: 'white',
              position: 'absolute',
              top: '2px',
              left: isBootable ? '18px' : '2px',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}></div>
          </div>
        </div>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {/* Custom Slider Track */}
                    <div
                      style={{
                        flex: 1,
                        height: '4px',
                        background: isSubmitting ? '#222' : '#333333',
                        borderRadius: '2px',
                        position: 'relative',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s ease',
                        opacity: isSubmitting ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.background = '#444444';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.background = '#333333';
                        }
                      }}
                      onMouseDown={(e) => {
                        if (isSubmitting) return;
                        if (e.target === e.currentTarget) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const percent = Math.max(0, Math.min(1, clickX / rect.width));
                          const newValue = Math.round(100 + percent * (maxVolumeSize - 100));
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setError(null);
                          e.preventDefault();
                        }
                      }}
                    >
                      {/* Thumb */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((size - 100) / (maxVolumeSize - 100)) * 100}%`,
                          top: '-5px',
                          width: '14px',
                          height: '14px',
                          background: isSubmitting ? '#666' : '#e0eacc',
                          borderRadius: '50%',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
                          transition: 'background 0.2s ease, opacity 0.2s ease',
                          pointerEvents: 'none',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                      />
                      {/* Invisible Hit Area */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((size - 100) / (maxVolumeSize - 100)) * 100}%`,
                          top: '-15px',
                          width: '34px',
                          height: '34px',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
                          zIndex: 1,
                        }}
                        onMouseDown={(e) => {
                          if (isSubmitting) return;
                          const trackRect = e.currentTarget.parentElement.getBoundingClientRect();
                          
                          const handleMouseMove = (moveEvent) => {
                            const newPercent = Math.max(0, Math.min(1, (moveEvent.clientX - trackRect.left) / trackRect.width));
                            const newVal = Math.round(100 + newPercent * (maxVolumeSize - 100));
                            setSize(newVal);
                            setSizeInputValue(`${newVal} GB`);
                          };
                          
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    {/* Increment/Decrement Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        style={{
                          width: '18px',
                          height: '12px',
                          background: '#333',
                          border: 'none',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          color: '#999',
                          transition: 'all 0.1s',
                          borderRadius: '2px 2px 0 0',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValue = Math.min(size + 100, maxVolumeSize);
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setIsSizeInputValid(true);
                          setSizeError('');
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#333';
                          e.target.style.color = '#999';
                        }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        style={{
                          width: '18px',
                          height: '12px',
                          background: '#333',
                          border: 'none',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          color: '#999',
                          transition: 'all 0.1s',
                          borderRadius: '0 0 2px 2px',
                          opacity: isSubmitting ? 0.5 : 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValue = Math.max(size - 100, 100);
                          setSize(newValue);
                          setSizeInputValue(`${newValue} GB`);
                          setIsSizeInputValid(true);
                          setSizeError('');
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#333';
                          e.target.style.color = '#999';
                        }}
                      >
                        ▼
                      </button>
                    </div>
      <input
        type="text"
        value={sizeInputValue}
        onChange={handleSizeInputChange}
        onFocus={(e) => {
          handleSizeInputFocus(e);
          e.target.style.borderColor = '#e0eacc';
          e.target.style.backgroundColor = '#252525';
          e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
        }}
        onBlur={(e) => {
          handleSizeInputBlur(e);
          e.target.style.borderColor = isSizeInputValid ? '#333' : '#ff6b6b';
          e.target.style.backgroundColor = '#1a1a1a';
          e.target.style.boxShadow = 'none';
        }}
        onKeyDown={handleSizeInputKeyDown}
        disabled={isSubmitting}
        style={{
          ...styles.input,
          width: '100px',
          textAlign: 'center',
          borderColor: isSizeInputValid ? '#333' : '#ff6b6b',
          transition: 'all 0.15s',
        }}
      />
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingRight: '112px' }}>
      <span style={{ color: '#444', fontSize: '10px' }}>100 GB</span>
      <span style={{ color: '#444', fontSize: '10px' }}>{maxVolumeSize.toLocaleString()} GB</span>
    </div>
    {sizeError && <div style={styles.errorText}>{sizeError}</div>}
  </div>
)}

                {/* Clone mode - inherited size display */}
                {mode === 'clone' && (
                  <div style={{ marginBottom: '18px' }}>
                    <label style={styles.label}>Size</label>
                    <div style={{
                      padding: '12px 14px',
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#9ca3af',
                      fontSize: '14px',
                    }}>
                      {sourceVolume?.size} GB (inherited from source)
                    </div>
                  </div>
                )}

                {/* OS Image Dropdown - When bootable is enabled */}
                {mode === 'create' && isBootable && !isBootableDisabled && (
                  <div style={{ ...styles.dropdown, marginBottom: '18px' }} ref={imageDropdownRef}>
                    <label style={styles.label}>
                      OS Image<span style={styles.required}>*</span>
                    </label>
                    {loadingStates.fetchingImages ? (
                      <div style={{
                        ...styles.dropdownTrigger,
                        cursor: 'default',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ ...styles.spinner, width: '14px', height: '14px' }}></div>
                          <span style={{ color: '#6b7280' }}>Loading images...</span>
                        </div>
                      </div>
                    ) : filteredImages.length > 0 ? (
                      <>
                        <div
                          style={{
                            ...styles.dropdownTrigger,
                            borderColor: isImageDropdownOpen ? '#e0eacc' : imageError ? '#ff6b6b' : '#333',
                          }}
                          onClick={() => !isSubmitting && setIsImageDropdownOpen(!isImageDropdownOpen)}
                        >
                          <span style={{ color: selectedImage ? 'white' : '#6b7280' }}>
                            {selectedImage 
                              ? filteredImages.find(img => img.name === selectedImage)?.name || selectedImage
                              : 'Select OS image'}
                          </span>
                          <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                        </div>
   {isImageDropdownOpen && !isSubmitting && (
  <div 
    style={{
      ...styles.dropdownMenu,
      maxHeight: '192px',
      top: 'auto',
      bottom: '100%',
      marginTop: '0',
      marginBottom: '4px',
    }} 
    className="volume-modal-scrollbar"
  >
    {filteredImages.map((img) => (
      <div
        key={img.name}
        style={{
          ...styles.dropdownItem,
          backgroundColor: selectedImage === img.name ? '#252525' : 'transparent',
        }}
        onClick={() => handleImageSelect(img.name)}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
        onMouseLeave={(e) => e.target.style.backgroundColor = selectedImage === img.name ? '#252525' : 'transparent'}
      >
        {sanitizeText(img.name)}
      </div>
    ))}
  </div>

                        )}
                      </>
                    ) : (
                      <div style={styles.errorBox}>
                        <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px' }} />
                        <span style={{ color: '#fca5a5', fontSize: '13px' }}>
                          No OS images available for this region
                        </span>
                      </div>
                    )}
                    {imageError && <div style={styles.errorText}>{imageError}</div>}
                  </div>
                )}

                {/* Info for advanced regions - bootable not supported */}
                {mode === 'create' && isBootableDisabled && (
                  <div style={styles.infoBox}>
                    <p style={styles.infoText}>
                      <FaInfoCircle style={{ marginRight: '8px' }} />
                      Bootable volumes not available for {selectedRegion}. OS is attached during VM creation.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT PANEL - Summary */}
          <div style={styles.rightPanel}>
            <h3 style={{
              color: '#6b7280',
              fontSize: '11px',
              letterSpacing: '1.5px',
              marginTop: 0,
              marginBottom: '24px',
            }}>SUMMARY</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={styles.summaryLabel}>Volume Name</p>
              <p style={styles.summaryValue}>{name || '—'}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={styles.summaryLabel}>Configuration</p>
              <p style={styles.summaryValue}>
                {mode === 'clone' ? sourceVolume?.size : size} GB {volumeType}
              </p>
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>{selectedRegion}</p>
            </div>

            {isBootable && selectedImage && (
              <div style={{ marginBottom: '20px' }}>
                <p style={styles.summaryLabel}>OS Image</p>
                <p style={{ color: 'white', fontSize: '14px', margin: 0 }}>
                  {filteredImages.find(i => i.name === selectedImage)?.name || selectedImage}
                </p>
              </div>
            )}

            <div style={{ borderTop: '1px solid #222', paddingTop: '20px', marginBottom: '24px' }}>
              <p style={styles.summaryLabel}>Monthly Cost</p>
              <p style={styles.priceValue}>{currentPricing.symbol}{currentPricing.monthly}</p>
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                {currentPricing.symbol}{currentPricing.perGB.toFixed(4)}/GB/month
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting || 
                !!nameError || 
                !!sizeError || 
                !!imageError || 
                !!volumeTypeError || 
                !!statusError ||
                loadingStates.checkingStatus ||
                !validationResults.nameValid || 
                !validationResults.sizeValid || 
                !validationResults.imageValid || 
                !validationResults.regionValid || 
                !validationResults.volumeTypeValid || 
                !validationResults.statusValid
              }
              style={{
                ...styles.submitBtn,
                opacity: (
                  isSubmitting || 
                  !!nameError || 
                  !!sizeError || 
                  !!imageError || 
                  !!volumeTypeError || 
                  !!statusError ||
                  loadingStates.checkingStatus ||
                  !validationResults.nameValid || 
                  !validationResults.sizeValid || 
                  !validationResults.imageValid || 
                  !validationResults.regionValid || 
                  !validationResults.volumeTypeValid || 
                  !validationResults.statusValid
                ) && !isSubmitting ? 0.5 : 1,
              }}
            >
              {renderButtonContent()}
            </button>
            
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateVolumeModal;